/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ActionDispatcher,
  ActionFlow,
  createFlow,
  exitFlow,
  FormState,
  LogicalAction,
  resetPrivilegedTokensAndUpdate,
} from '@gh/common-shared';
import { yesNoCancelConfirmation } from '@gh/core-ui';
import { CommonActions } from '../common-actions.service';
import { FormActions, FormFlowState } from './form-actions.service';

@Injectable()
export class FormActionFlows {
  constructor(private commonActions: CommonActions, private formActions: FormActions) {

  }

  updateFlow<S extends FormFlowState<any>>(action: LogicalAction<S>,
                                           executor?: ProcedureFn<S>): ActionFlow<S> {
    return createFlow({
      'start': action.bindAs('save.success', 'save.fail'),
      'save.success': (dispatch: ActionDispatcher, flowState: S) => {
        if (flowState.form instanceof FormState) {
          resetPrivilegedTokensAndUpdate(flowState.form);
        }

        if (flowState.form.resetHighlight) {
          flowState.form.resetHighlight();
        }

        if (executor) {
          executor(flowState);
        }

        dispatch('finish', flowState);
      },
      '*': exitFlow(),
    });
  }

  recalculateFlow<S extends FormFlowState<any>>(action: LogicalAction<S>,
                                                executor?: ProcedureFn<S>): ActionFlow<S> {
    return createFlow({
      'start': action.bindAs('save.success', 'save.fail'),
      'save.success': (dispatch: ActionDispatcher, flowState: S) => {
        if (executor) {
          executor(flowState);
        }

        dispatch('finish', flowState);
      },
      '*': exitFlow(),
    });
  }

  deactivateFlow<S extends FormFlowState<any>>(save: LogicalAction<S>): ActionFlow<S> {
    const exitWithoutConfirmation = (value: boolean) => exitFlow<any>({ confirmation: value });

    return createFlow({
      'start': this.formActions.shouldSave().bindAs('shouldSave.yes', 'shouldSave.no'),
      'shouldSave.yes': this.commonActions.confirmation(yesNoCancelConfirmation('msg_unsaved_info'), {
        yes: 'confirmation.yes',
        no: 'confirmation.no',
        cancel: 'confirmation.cancel',
      }),
      'shouldSave.no': exitWithoutConfirmation(true),
      'confirmation.yes': save.bindAs('save.success', 'save.fail'),
      'save.success': exitWithoutConfirmation(true),
      'confirmation.no': exitWithoutConfirmation(true),
      '*': exitWithoutConfirmation(false),
    });
  }
}
